import type { PilotRepository } from "~application/devices/ports/pilot-repository.ts";
import type { Modulator } from "~domain/devices/models/modulators/modulator-model.ts";
import type { PilotComponent } from "~domain/devices/models/pilot/pilot-component-model.ts";
import type { PilotLastConnection } from "~domain/devices/models/pilot/pilot-connection-status-model.ts";
import type { PilotDataFrame } from "~domain/devices/models/pilot/pilot-data-frame-model.ts";
import type { PilotGprsConso } from "~domain/devices/models/pilot/pilot-gprs-conso-model.ts";
import type { Pilot, PilotHwId } from "~domain/devices/models/pilot/pilot-model.ts";
import type { SiteId } from "~domain/sites/models/site-model.ts";
import type { BackofficeApi } from "~infrastructure/api/backoffice/http/backoffice-tsrest-client.ts";

import {
  DomainError,
  DomainErrorCode,
  DomainNotFoundError,
  DomainTransportError,
  DomainUnauthorizedError,
} from "~domain/global/errors/domain-error.ts";
import { ModulatorMapper } from "~infrastructure/devices/mappers/modulator-mapper.ts";
import { PilotMapper } from "~infrastructure/devices/mappers/pilot-mapper.ts";
import { isDefined } from "~utils/collection.ts";

export class BackofficePilotRepository implements PilotRepository {
  constructor(private readonly backofficeApi: BackofficeApi) {}

  async findPilot(pilotHwId: PilotHwId): Promise<Pilot> {
    const response = await this.backofficeApi.devices.findPilot({
      params: {
        pilotHwId: pilotHwId,
      },
    });

    if (response.status === 200) {
      const pilot = PilotMapper.mapBackofficePilotToDomain(response.body);

      if (!pilot) {
        throw new DomainNotFoundError(`Unable to found pilot with hw id ${pilotHwId}`);
      }

      return pilot;
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findPilotGprsConso(pilot: Pilot): Promise<PilotGprsConso> {
    const response = await this.backofficeApi.devices.findPilotGprsConso({
      params: {
        pilotHwId: pilot.hwId,
      },
    });

    if (response.status === 200) {
      const pilotGprsConso = PilotMapper.mapBackofficePilotGprsConsoToDomain(response.body);
      return pilotGprsConso;
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findPilotLastConnection(pilot: Pilot): Promise<PilotLastConnection> {
    const response = await this.backofficeApi.devices.findPilotLastConnection({
      params: {
        pilotHwId: pilot.hwId,
      },
    });

    if (response.status === 200) {
      return PilotMapper.mapBackofficePilotLastConnectionToDomain(response.body);
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findPilotComponents(pilot: Pilot): Promise<Array<PilotComponent>> {
    const response = await this.backofficeApi.devices.findPilotComponents({
      params: {
        pilotHwId: pilot.hwId,
      },
    });

    if (response.status === 200) {
      return PilotMapper.mapBackofficePilotComponentsToDomain(response.body);
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findPilotDataFrames(pilot: Pilot): Promise<Array<PilotDataFrame>> {
    const response = await this.backofficeApi.devices.findPilotDataFrames({
      params: {
        pilotHwId: pilot.hwId,
      },
    });

    if (response.status === 200) {
      return PilotMapper.mapBackofficePilotDataFramesToDomain(response.body);
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findPilotModulators(pilot: Pilot): Promise<Array<Modulator>> {
    const response = await this.backofficeApi.devices.findPilotModulators({
      params: {
        pilotHwId: pilot.hwId,
      },
    });

    if (response.status === 200) {
      return response.body.map(ModulatorMapper.fromBackofficeModulatorToDomainModulator).filter(isDefined);
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async updatePilotVersion(siteId: SiteId, pilot: Pilot): Promise<void> {
    const response = await this.backofficeApi.devices.updatePilotVersion({
      params: {
        siteId: siteId,
        serialNumber: pilot.serialNumber,
      },
      body: undefined,
    });

    if (response.status === 200) {
      return;
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }
}
